import { RectangleStackIcon } from "@heroicons/react/24/outline";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { ActionButton } from "@via/components/action-button/action-button";
import DeleteModal from "@via/components/delete-modal/delete-modal";
import Drawer from "@via/components/drawer";
import {
  MessageBanner,
  BannerType,
} from "@via/components/message-banner/message-banner";
import SelectComponent from "@via/components/select/select";
import Table, { DrawerSize } from "@via/components/table/table";
import TableContextMenu from "@via/components/table/table-context-menu";
import CreateFieldForm from "@via/pages/projects/metadata/configuration/fields/create-field-form";
import EditFieldForm from "@via/pages/projects/metadata/configuration/fields/edit-field-form";
import {
  useVmetaFields,
  useVmetaCollections,
  useDeleteVmetaField,
} from "@via/pages/projects/metadata/metadata.api.tsx";
import {
  METADATA_CONFIGURATION_TABS,
  METADATA_FIELDS_TABLE,
  ROUTES,
} from "@via/utils/constants";
import { formatDateString } from "@via/utils/helpers";
import { SelectOption } from "@via/utils/interfaces";
import { createOneLineColumnFactory } from "@via/utils/table-utils";
import { VmetaField } from "common";
import { type FC, useCallback, useMemo, useState, useEffect } from "react";
import { useDebounceCallback } from "usehooks-ts";
import CreateReferenceFieldModal from "@via/pages/projects/metadata/configuration/fields/create-reference-field-modal";
import { useRegisterCortexContext } from "@via/hooks/useRegisterCortexContext";
import { useLocation } from "react-router-dom";

interface FieldsProps {
  projectId: string | number | null;
  activeTab?: string;
  initialSelectedCollectionName?: string | null;
  onCollectionSelected?: () => void;
}

type VmetaFieldWithOtherFields = VmetaField & {
  owner: {
    username: string;
  };
  createdAt: Date;
  updatedAt: Date;
  __v: number;
  lastUpdatedUser: {
    username: string;
  };
  stickyColumn: string;
};

const columnHelper = createColumnHelper<VmetaFieldWithOtherFields>();

const pageSize = 10;

const Fields: FC<FieldsProps> = ({
  projectId,
  activeTab,
  initialSelectedCollectionName,
  onCollectionSelected,
}) => {
  const location = useLocation();
  const createOneLineColumn = createOneLineColumnFactory(columnHelper);
  const [selectedCollection, setSelectedCollection] =
    useState<SelectOption | null>(null);

  const [page, setPage] = useState(0);
  const [search, setSearch] = useState(initialSelectedCollectionName || "");

  const [cumulativeCollections, setCumulativeCollections] = useState<
    SelectOption[]
  >([]);

  const [isCreateFieldDrawerOpen, setIsCreateFieldDrawerOpen] = useState(false);
  const [isCreateReferenceFieldModalOpen, setIsCreateReferenceFieldModalOpen] =
    useState(false);

  const [tablePage, setTablePage] = useState(0);
  const [tablePageSize, setTablePageSize] = useState(100);

  const [fieldToBeEdited, setFieldToBeEdited] = useState<VmetaField | null>(
    null
  );
  const [fieldToBeDeleted, setFieldToBeDeleted] =
    useState<VmetaFieldWithOtherFields | null>(null);

  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >({});
  const {
    mutateAsync: deleteVmetaField,
    isPending: isDeleteVmetaFieldLoading,
  } = useDeleteVmetaField();

  const { data: collections, isLoading: isCollectionsLoading } =
    useVmetaCollections(
      page,
      pageSize,
      [],
      [
        {
          id: "projectID",
          value: projectId,
        },
        {
          id: "name",
          value: search,
        },
      ]
    );

  const { data: fields, isLoading: isFieldsLoading } = useVmetaFields(
    tablePage,
    tablePageSize,
    [],
    [
      {
        id: "collectionID",
        value: selectedCollection?.value,
      },
    ]
  );

  /** ===== Auto-select field from collections tab ===== */

  useEffect(() => {
    if (initialSelectedCollectionName && cumulativeCollections.length > 0) {
      const match = cumulativeCollections.find(
        o => o.label === initialSelectedCollectionName
      );
      if (match && match.value !== selectedCollection?.value) {
        setSelectedCollection(match);
        setSearch("");
        setPage(0);
        onCollectionSelected?.();
      }
    }
  }, [
    cumulativeCollections,
    initialSelectedCollectionName,
    onCollectionSelected,
    selectedCollection,
  ]);

  useEffect(() => {
    if (!collections) return;

    setCumulativeCollections(prevCollections => {
      const newData = collections.data ?? [];

      if (!newData.length) return prevCollections;

      // Append new results while preventing duplicates
      const existingIds = new Set(prevCollections.map(c => c.value));
      const newCollections = newData
        .map(({ label, _id }) => ({ label, value: _id }))
        .filter(c => !existingIds.has(c.value));

      return [...prevCollections, ...newCollections];
    });
  }, [collections?.data, page, search]);

  const fetchMoreCollections = useCallback(() => {
    setPage(currentPage => {
      const nextPage = currentPage + 1;
      const maxPage = Math.ceil((collections?.results ?? 0) / pageSize) - 1;
      return nextPage <= maxPage ? nextPage : currentPage;
    });
  }, [collections?.results]);

  const debouncedHandleInputChange = useDebounceCallback((value: string) => {
    if (value === search) return;
    setPage(0);
    setSearch(value);
    setCumulativeCollections([]);
  }, 300);

  const handleContextMenuAction = (
    optionName: string,
    field: VmetaFieldWithOtherFields
  ): void => {
    switch (optionName) {
      case "Edit Field": {
        const {
          active,
          owner,
          createdAt,
          updatedAt,
          lastUpdatedUser,
          __v,
          ...rest
        } = field;
        setFieldToBeEdited(rest);
        break;
      }
      case "Delete Field":
        setFieldToBeDeleted(field);
        break;
    }
  };

  const validateIdentifierFields = (fields: VmetaField[]) => {
    const identifierFields = fields.filter(field => field.identifier === true);

    if (identifierFields.length === 0) {
      return "No identifier field is set. Please mark one field as identifier.";
    }

    if (identifierFields.length > 1) {
      return "Multiple identifier fields are set. Only one field should be marked as identifier.";
    }

    return null;
  };

  const columns = [
    createOneLineColumn("name", "Name (used in API URLs)"),
    createOneLineColumn("label", "Label"),
    createOneLineColumn("type", "Type"),
    createOneLineColumn("required", "Required", {
      cellRenderer: value => value?.toString(),
    }),
    createOneLineColumn("identifier", "Identifier", {
      cellRenderer: value => value?.toString(),
    }),
    createOneLineColumn("collectionID", "Collection Id"),
    createOneLineColumn("_id", "Id"),
    createOneLineColumn("owner.username", "Owner"),
    createOneLineColumn("createdAt", "Created At", {
      cellRenderer: value => formatDateString(value as Date, "LLL"),
    }),
    createOneLineColumn("updatedAt", "Updated At", {
      cellRenderer: value => formatDateString(value as Date, "LLL"),
    }),
    createOneLineColumn("description", "Description"),
    createOneLineColumn("unit", "Unit"),
    createOneLineColumn("namingPattern", "Naming Pattern"),
    createOneLineColumn("barcode", "Barcode"),
    createOneLineColumn("barcode_prefix", "Barcode Prefix"),
    createOneLineColumn("unique", "Unique", {
      cellRenderer: value => value?.toString(),
    }),
    createOneLineColumn("hidden", "Hidden", {
      cellRenderer: value => value?.toString(),
    }),
    createOneLineColumn("deprecated", "Deprecated", {
      cellRenderer: value => value?.toString(),
    }),
    createOneLineColumn("checkvalid", "Check Valid"),
    createOneLineColumn("ontology", "Ontology"),
    createOneLineColumn("order", "Order"),
    createOneLineColumn("active", "Active", {
      cellRenderer: value => value?.toString(),
    }),
    createOneLineColumn("descriptive", "Descriptive", {
      cellRenderer: value => value?.toString(),
    }),
    createOneLineColumn("grouped", "Grouped"),
    createOneLineColumn("minlength", "Minlength"),
    createOneLineColumn("maxlength", "Maxlength"),
    createOneLineColumn("various", "Various"),
    createOneLineColumn("ref", "Ref"),
    createOneLineColumn("enum", "Enum"),
    createOneLineColumn("min", "Min"),
    createOneLineColumn("max", "Max"),
    createOneLineColumn("lowercase", "Lowercase", {
      cellRenderer: value => value?.toString(),
    }),
    createOneLineColumn("uppercase", "Uppercase", {
      cellRenderer: value => value?.toString(),
    }),
    createOneLineColumn("trim", "Trim", {
      cellRenderer: value => value?.toString(),
    }),
    createOneLineColumn("header", "Header", {
      cellRenderer: value => value?.toString(),
    }),
    columnHelper.accessor("stickyColumn", {
      header: () => "Options",
      cell: ({ row }) => (
        <TableContextMenu
          options={METADATA_FIELDS_TABLE}
          onSelect={option => {
            handleContextMenuAction(option.name, row.original);
          }}
        />
      ),
      enableSorting: false,
    }),
  ] as Array<ColumnDef<VmetaFieldWithOtherFields>>;

  const displayWarning = () => {
    if (selectedCollection && fields?.data) {
      const validationMessage = validateIdentifierFields(fields.data);
      return validationMessage ? (
        <MessageBanner messages={validationMessage} type={BannerType.Warning} />
      ) : null;
    }
    return null;
  };

  const routePattern = useMemo(() => {
    if (location.pathname.startsWith("/sample-trackers/")) {
      return ROUTES.SAMPLE_TRACKERS_DETAIL;
    }
    return ROUTES.PROJECT_METADATA;
  }, [location.pathname]);

  const getFieldsContext = useCallback(() => {
    const fieldsList = (fields?.data || []) as VmetaFieldWithOtherFields[];

    return {
      project: {
        id: projectId,
      },
      collection: selectedCollection
        ? {
            id: selectedCollection.value as string,
            name: selectedCollection.label,
          }
        : null,
      fields: fieldsList.map(field => ({
        id: field._id,
        name: field.name,
        label: field.label,
        type: field.type,
        required: field.required,
        identifier: field.identifier,
        collectionID: field.collectionID,
        description: field.description,
        unit: field.unit,
        unique: field.unique,
        hidden: field.hidden,
        deprecated: field.deprecated,
        active: field.active,
        owner: field.owner?.username || "",
        createdAt: field.createdAt,
        updatedAt: field.updatedAt,
      })),
      total: fields?.results || 0,
      currentPage: tablePage,
      pageSize: tablePageSize,
      validation:
        selectedCollection && fields?.data
          ? validateIdentifierFields(fields.data)
          : null,
    };
  }, [projectId, selectedCollection, fields, tablePage, tablePageSize]);

  useRegisterCortexContext({
    routePattern,
    contextName: "Fields Configuration",
    getContext: getFieldsContext,
    systemPrompt:
      "You are analyzing metadata fields configuration page. Help answer questions about fields, their types, validation rules, and field management.",
    enabled:
      !!selectedCollection &&
      !!fields &&
      activeTab === METADATA_CONFIGURATION_TABS.FIELDS,
  });

  return (
    <>
      <div className="flex items-center justify-between mt-5 gap-4">
        <div className="flex-1">
          <SelectComponent
            name="collectionId"
            options={cumulativeCollections}
            isLoading={isCollectionsLoading}
            placeholder="Select Collection"
            prefixIcon={<RectangleStackIcon />}
            onFetchMoreOptions={fetchMoreCollections}
            onInputChange={inputValue => debouncedHandleInputChange(inputValue)}
            enableClear={false}
            value={selectedCollection}
            onChange={data => {
              setSelectedCollection(data as SelectOption);
            }}
          />
        </div>
        {selectedCollection && (
          <div className="flex justify-end gap-2">
            <ActionButton
              button="Add Reference Field"
              primary={true}
              onClick={() => setIsCreateReferenceFieldModalOpen(true)}
            />
            <ActionButton
              button="Add Field"
              primary={true}
              onClick={() => setIsCreateFieldDrawerOpen(true)}
            />
          </div>
        )}
      </div>
      {displayWarning()}
      {selectedCollection && (
        <div className="mt-6 overflow-auto h-100vh">
          <Table
            isLoading={isFieldsLoading}
            data={fields?.data || []}
            columns={columns}
            page={tablePage}
            setPage={setTablePage}
            pageSize={tablePageSize}
            setPageSize={setTablePageSize}
            pageSizeOptions={[100]}
            total={fields?.results || 0}
            enableGlobalFilter
            applyColumnSize
            customTableClassName="w-max whitespace-nowrap"
            emptyMessage="No data available."
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
            displayCustomColumnVisibility={true}
          />
        </div>
      )}
      {fieldToBeDeleted && (
        <DeleteModal
          item={fieldToBeDeleted}
          setItem={setFieldToBeDeleted}
          deleteFunction={() =>
            deleteVmetaField({ id: fieldToBeDeleted._id as string })
          }
          isLoading={isDeleteVmetaFieldLoading}
          title="Delete Field"
        />
      )}
      <Drawer
        id="create-field"
        open={isCreateFieldDrawerOpen}
        setOpen={setIsCreateFieldDrawerOpen}
        size={DrawerSize.Big}>
        <CreateFieldForm
          setOpen={setIsCreateFieldDrawerOpen}
          collectionId={selectedCollection?.value as string}
        />
      </Drawer>
      <Drawer
        id="edit-field"
        open={!!fieldToBeEdited}
        setOpen={() => setFieldToBeEdited(null)}
        size={DrawerSize.Big}>
        <EditFieldForm
          setOpen={() => setFieldToBeEdited(null)}
          field={fieldToBeEdited}
        />
      </Drawer>
      {isCreateReferenceFieldModalOpen && (
        <CreateReferenceFieldModal
          open={isCreateReferenceFieldModalOpen}
          setOpen={setIsCreateReferenceFieldModalOpen}
          collectionId={selectedCollection?.value as string}
          projectId={projectId as string}
        />
      )}
    </>
  );
};

export default Fields;
